import { parametric, pathSubscription, subscription } from 'nike-re-framejs';
import { Favorite, OverviewMatchFavorite, OverviewTournamentFavorite } from './Favorite';
import { List, Map, Set } from 'immutable';
import { allVisibleSportMatches$$, sportByName$$, sports$ } from '../push/overview/subs';
import { MatchIdentity } from '../dto';
import { allMatchIds$, allVisibleMatches$ } from '../subs';

export const favoritesTournaments$ = pathSubscription(['account', 'userFavourites', 'turboLive', 'tournaments']);
export const favoritesMatches$ = pathSubscription(['account', 'userFavourites', 'turboLive', 'matches']);

export const favoritesMatchesById$ = subscription(
    (favorites: List<OverviewMatchFavorite>) =>
        Map((favorites || []).map((favorite: OverviewMatchFavorite) => [favorite.get('id'), favorite])),
    favoritesMatches$
);

export const favoritesTournamentsById$ = subscription(
    (favorites: List<Favorite>) =>
        Map((favorites || []).map((favorite: OverviewTournamentFavorite) => [favorite.get('tournamentId'), favorite])),
    favoritesTournaments$
);

export const favoritesMatchIdSet$$ = parametric(sportCode =>
    subscription(
        (
            sport: Map<string, any>,
            matchesById: Map<string, OverviewMatchFavorite>,
            tournamentsById: Map<string, OverviewTournamentFavorite>
        ) => {
            const sportIdByName = sport?.get('id');
            return matchesById
                .filter(
                    favorite =>
                        favorite.get('sportId') === sportIdByName && !tournamentsById.has(favorite.get('tournamentId'))
                )
                .keySeq()
                .toSet();
        },
        sportByName$$(sportCode),
        favoritesMatchesById$,
        favoritesTournamentsById$
    )
);

export const favoritesLiveMatchIds$$ = parametric(sportCode =>
    subscription(
        (favoriteIdSet: Set<string>, liveMatchIds: Map<string, Object>) =>
            liveMatchIds
                ?.keySeq()
                .toSet()
                .filter(id => favoriteIdSet.has(id))
                .toArray() || [],

        favoritesMatchIdSet$$(sportCode),
        allMatchIds$
    )
);

export const favoriteTournamentIds$$ = parametric(sportCode =>
    subscription(
        (favoritesById: Map<string, OverviewTournamentFavorite>) =>
            favoritesById
                .filter(favorite => favorite.get('sportCode') === sportCode)
                .keySeq()
                .toArray(),
        favoritesTournamentsById$
    )
);

export const isFavoriteMatch$$ = parametric(matchId =>
    subscription(
        (favoritesById: Map<string, OverviewMatchFavorite>) => favoritesById.has(matchId),
        favoritesMatchesById$
    )
);

export const isFavoriteTournament$$ = parametric(tournamentId =>
    subscription(
        (favoritesById: Map<string, OverviewTournamentFavorite>) => favoritesById.has(tournamentId),
        favoritesTournamentsById$
    )
);

export const isGlobalFavoriteMarket$$ = parametric(marketId =>
    subscription(db => db.getIn(['account', 'userFavourites', 'turboLive', 'marketsGlobal'])?.has(marketId))
);

export const activeFavoriteMatchIdentities$ = subscription(
    (
        favoriteMatchesById: Map<string, OverviewMatchFavorite>,
        favoriteTournamentsById: Map<string, OverviewTournamentFavorite>,
        liveMatchesById: Map<string, MatchIdentity>
    ) =>
        liveMatchesById?.filter(
            ({ id, tournamentId }) =>
                favoriteMatchesById.some(matchFavorite => matchFavorite.get('id') === id) ||
                favoriteTournamentsById.some(
                    tournamentFavorite => tournamentFavorite.get('tournamentId') === tournamentId
                )
        ),
    favoritesMatchesById$,
    favoritesTournamentsById$,
    allVisibleMatches$
);

export const activeFavoriteMatchCount$ = subscription(
    (liveMatchesById: Map<string, MatchIdentity>) => liveMatchesById?.size || 0,
    activeFavoriteMatchIdentities$
);

export const activeFavoriteSportIdSet$ = subscription(
    (allMatchIds: Map<string, MatchIdentity>) =>
        allMatchIds
            ?.toList()
            .map(match => match.sportId)
            .toSet() || Set(),
    activeFavoriteMatchIdentities$
);

export const activeFavoriteSportCodes$ = subscription(
    (sports: Map<string, any>, sportIdsWithActiveFavorites: Set<string>) => {
        return sports?.size
            ? sportIdsWithActiveFavorites
                  .sortBy(id => sports.find((v, k) => k === id)?.get('order'))
                  .map(id => {
                      return sports.getIn([id, 'slug'], '').substring(1);
                  })
                  .filter(code => !!code)
                  .toArray()
            : [];
    },
    sports$,
    activeFavoriteSportIdSet$
);

export const overviewLoaded$ = subscription(
    (activeSportCodes, overviewLoaded) => {
        const overviewSportCodesArray = overviewLoaded?.keySeq()?.toArray();
        return (
            activeSportCodes && overviewLoaded && activeSportCodes.every(elem => overviewSportCodesArray.includes(elem))
        );
    },
    activeFavoriteSportCodes$,
    pathSubscription(['turboLive', 'overview'])
);

export const hasFavoriteMatches$ = parametric(sportCode =>
    subscription(
        (
            favoriteMatchesById: Map<string, OverviewMatchFavorite>,
            favoriteTournamentsById: Map<string, OverviewTournamentFavorite>,
            visibleSportMatches: Map<string, any>
        ) => {
            return visibleSportMatches?.filter(
                (m, id) =>
                    favoriteMatchesById.some(matchFavorite => matchFavorite.get('id') === id) ||
                    favoriteTournamentsById.some(
                        tournamentFavorite => tournamentFavorite.get('tournamentId') === m.get('tournamentId')
                    )
            );
        },
        favoritesMatchesById$,
        favoritesTournamentsById$,
        allVisibleSportMatches$$(sportCode)
    )
);

export const overviewMarketApiSucess$ = pathSubscription([
    'account',
    'userFavourites',
    'turboLive',
    'overviewMarketsSucess'
]);
